import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../api/client';
import type {
  AiTemplateDraft,
  DocumentSendListItem,
  MergeFieldReference,
  PackageDetail,
  PackageListItem,
  PackageSend,
  PublicPackageSend,
  SignatureVerification,
  TemplateDetail,
  TemplateKind,
  TemplateListItem,
  ValidityReportItem
} from './types';

// ─── Templates ───────────────────────────────────────────────────────────────

/** The merge tokens the app fills, with descriptions + examples (for the AI prompt). */
export function useMergeFieldReference() {
  return useQuery({
    queryKey: ['merge-field-reference'],
    queryFn: async () => {
      const res = await apiClient.get<MergeFieldReference>('/api/v1/document-templates/merge-fields');
      return res.data;
    },
    staleTime: 5 * 60_000
  });
}

export function useTemplateList(includeInactive = false) {
  return useQuery({
    queryKey: ['templates', { includeInactive }],
    queryFn: async () => {
      const res = await apiClient.get<TemplateListItem[]>('/api/v1/document-templates', {
        params: { includeInactive }
      });
      return res.data;
    },
    staleTime: 30_000
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['template', id],
    queryFn: async () => {
      const res = await apiClient.get<TemplateDetail>(`/api/v1/document-templates/${id}`);
      return res.data;
    },
    enabled: !!id
  });
}

interface UploadTemplateInput {
  file: File;
  name: string;
  kind: TemplateKind;
  isEffectiveDateBound: boolean;
  requiresSignature: boolean;
}

export function useUploadTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadTemplateInput) => {
      const fd = new FormData();
      fd.append('file', input.file);
      fd.append('name', input.name);
      fd.append('kind', input.kind);
      fd.append('isEffectiveDateBound', String(input.isEffectiveDateBound));
      fd.append('requiresSignature', String(input.requiresSignature));
      const res = await apiClient.post<TemplateDetail>('/api/v1/document-templates', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] })
  });
}

// ─── Create with AI ──────────────────────────────────────────────────────────

/** Generate a draft from a natural-language description. Returns the draft only. */
export function useAiDraft() {
  return useMutation({
    mutationFn: async (description: string) => {
      const res = await apiClient.post<{ draft: AiTemplateDraft }>(
        '/api/v1/document-templates/ai-draft',
        { description }
      );
      return res.data.draft;
    }
  });
}

interface CreateFromDraftInput {
  draft: AiTemplateDraft;
  name: string;
  kind: TemplateKind;
  isEffectiveDateBound: boolean;
  requiresSignature: boolean;
}

/** Build a .docx from an approved draft and store it as a template. */
export function useCreateTemplateFromDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFromDraftInput) => {
      const res = await apiClient.post<TemplateDetail>(
        '/api/v1/document-templates/from-draft',
        input
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] })
  });
}

interface UpdateTemplateInput {
  id: string;
  name: string;
  kind: TemplateKind;
  isEffectiveDateBound: boolean;
  requiresSignature: boolean;
  isActive: boolean;
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateTemplateInput) => {
      await apiClient.put(`/api/v1/document-templates/${id}`, body);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.invalidateQueries({ queryKey: ['template', id] });
    }
  });
}

export function useDeactivateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/document-templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] })
  });
}

// ─── Packages ────────────────────────────────────────────────────────────────

export function usePackageList() {
  return useQuery({
    queryKey: ['packages'],
    queryFn: async () => {
      const res = await apiClient.get<PackageListItem[]>('/api/v1/document-packages');
      return res.data;
    },
    staleTime: 30_000
  });
}

export function usePackage(id: string | undefined) {
  return useQuery({
    queryKey: ['package', id],
    queryFn: async () => {
      const res = await apiClient.get<PackageDetail>(`/api/v1/document-packages/${id}`);
      return res.data;
    },
    enabled: !!id
  });
}

interface UpsertPackageInput {
  id?: string;
  name: string;
  description?: string;
  items: Array<{ templateId: string; sortOrder: number; isRequired: boolean }>;
}

export function useSavePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertPackageInput) => {
      const body = {
        name: input.name,
        description: input.description ?? null,
        items: input.items
      };
      if (input.id) {
        await apiClient.put(`/api/v1/document-packages/${input.id}`, body);
        return input.id;
      }
      const res = await apiClient.post<PackageDetail>('/api/v1/document-packages', body);
      return res.data.id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['packages'] });
      qc.invalidateQueries({ queryKey: ['package', id] });
    }
  });
}

/** Permanently deletes a bundle (and its items). Send history is unaffected. */
export function useDeletePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/document-packages/${id}`);
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['packages'] });
      qc.removeQueries({ queryKey: ['package', id] });
    }
  });
}

// ─── Sends ───────────────────────────────────────────────────────────────────

interface AdHocSendInput {
  customerId: string;
  visitId?: string | null;
  items: Array<{ templateId: string; mergeValues: Record<string, string> }>;
}

interface PackageSendInput {
  packageId: string;
  customerId: string;
  visitId?: string | null;
  mergeValues: Record<string, Record<string, string>>;
}

export function useSendAdHoc() {
  return useMutation({
    mutationFn: async (input: AdHocSendInput) => {
      const res = await apiClient.post<PackageSend>('/api/v1/document-sends/ad-hoc', input);
      return res.data;
    }
  });
}

export function useSendPackage() {
  return useMutation({
    mutationFn: async (input: PackageSendInput) => {
      const res = await apiClient.post<PackageSend>('/api/v1/document-sends/package', input);
      return res.data;
    }
  });
}

interface ResolveMergeValuesInput {
  customerId: string;
  visitId?: string | null;
  fieldNames?: string[];
}

export function useResolveMergeValues() {
  return useMutation({
    mutationFn: async (input: ResolveMergeValuesInput) => {
      const res = await apiClient.post<{ resolved: Record<string, string> }>(
        '/api/v1/document-sends/resolve-merge-values',
        input
      );
      return res.data;
    }
  });
}

// ─── Portal: signing status + validity (Steps C & D) ──────────────────────────

export function useDocumentSends() {
  return useQuery({
    queryKey: ['document-sends'],
    queryFn: async () => {
      const res = await apiClient.get<DocumentSendListItem[]>('/api/v1/documents');
      return res.data;
    },
    staleTime: 30_000
  });
}

export function useValidityReport() {
  return useQuery({
    queryKey: ['document-validity'],
    queryFn: async () => {
      const res = await apiClient.get<ValidityReportItem[]>('/api/v1/documents/validity');
      return res.data;
    },
    staleTime: 30_000
  });
}

/// Fetches the executed PDF with the bearer token and triggers a browser
/// download — a plain anchor href can't carry the Authorization header.
export async function downloadDocumentPdf(downloadUrl: string, fileName: string) {
  const res = await apiClient.get(downloadUrl, { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

// ─── Public ──────────────────────────────────────────────────────────────────

export function usePublicPackageSend(nonce: string | undefined) {
  return useQuery({
    queryKey: ['public-package-send', nonce],
    queryFn: async () => {
      const base = import.meta.env.VITE_API_BASE_URL;
      const res = await axios.get<PublicPackageSend>(`${base}/api/v1/public/package-sends/${nonce}`);
      return res.data;
    },
    enabled: !!nonce,
    retry: false
  });
}

export function usePublicSign(nonce: string) {
  return useMutation({
    mutationFn: async (input: { documentInstanceId: string; typedName: string; signerEmail: string }) => {
      const base = import.meta.env.VITE_API_BASE_URL;
      const res = await axios.post<{
        signatureId: string;
        documentInstanceId: string;
        packageComplete: boolean;
      }>(`${base}/api/v1/public/package-sends/${nonce}/sign`, input);
      return res.data;
    }
  });
}

export function usePublicDocumentView(nonce: string | undefined) {
  return useQuery({
    queryKey: ['public-document-view', nonce],
    queryFn: async () => {
      const base = import.meta.env.VITE_API_BASE_URL;
      const res = await axios.get<PublicPackageSend>(`${base}/api/v1/public/documents/${nonce}`);
      return res.data;
    },
    enabled: !!nonce,
    retry: false
  });
}

export function usePublicVerify(id: string | undefined) {
  return useQuery({
    queryKey: ['public-verify', id],
    queryFn: async () => {
      const base = import.meta.env.VITE_API_BASE_URL;
      const res = await axios.get<SignatureVerification>(`${base}/api/v1/public/verify/${id}`);
      return res.data;
    },
    enabled: !!id,
    retry: false
  });
}
